import { useCallback, useEffect, useRef, useState } from 'react';

/** Cada cuánto se refresca el contador. La pantalla no muestra segundos: solo decide si ya se pasó. */
const TICK_MS = 10_000;

/**
 * Cuánto lleva esperando la pantalla — milisegundos, o `null` mientras no arrancó — más un
 * `restart` para el botón de volver a consultar. Sin este tope una falla muda se ve como una espera.
 */
export function useWaitTimer(active: boolean): [number | null, () => void] {
    const startedAt = useRef<number | null>(null);
    const [msWaiting, setMsWaiting] = useState<number | null>(null);
    // Reiniciar tiene que RECREAR el intervalo, no solo mover el `ref`: el que estaba corriendo
    // capturó el instante de arranque viejo y en su próximo tick reescribía el tiempo original.
    const [restartToken, setRestartToken] = useState(0);

    const restart = useCallback(() => {
        startedAt.current = Date.now();
        setMsWaiting(0);
        setRestartToken((token) => token + 1);
    }, []);

    useEffect(() => {
        if (!active) {
            startedAt.current = null;
            return;
        }
        // `??=` y no una asignación directa: reiniciarlo en cada corrida del efecto dejaría la espera
        // eternamente joven y el tope no se alcanzaría nunca.
        startedAt.current ??= Date.now();
        const from = startedAt.current;
        const id = setInterval(() => {
            setMsWaiting(Date.now() - from);
        }, TICK_MS);
        return () => {
            clearInterval(id);
            // Sin esto, una espera nueva arrancaría con el valor de la anterior y la pantalla
            // mostraría "ya tardó demasiado" durante los primeros diez segundos.
            setMsWaiting(null);
        };
    }, [active, restartToken]);

    return [msWaiting, restart];
}
